import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, AlertTriangle, ChevronRight } from 'lucide-react';
import { getOwnershipChain, getInstitution } from '../api/client';

export default function OwnershipTree() {
  const { lei } = useParams<{ lei: string }>();

  const { data: institution, isLoading: institutionLoading } = useQuery({
    queryKey: ['institution', lei],
    queryFn: () => getInstitution(lei!),
    enabled: !!lei,
  });

  const { data: ownership, isLoading: ownershipLoading, error } = useQuery({
    queryKey: ['ownership-chain', lei],
    queryFn: () => getOwnershipChain(lei!),
    enabled: !!lei,
  });

  const isLoading = institutionLoading || ownershipLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (error || !ownership) {
    return (
      <div className="card p-12 text-center">
        <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Unable to Load Ownership Chain
        </h2>
        <p className="text-gray-500 mb-4">
          The graph database may not be available or configured.
        </p>
        <Link to={`/institution/${lei}`} className="btn-primary inline-block">
          Back to Institution
        </Link>
      </div>
    );
  }

  const isOwnParent = ownership.entity_lei === ownership.ultimate_parent_lei && ownership.chain.length === 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to={`/institution/${lei}`}
          className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {institution?.legal_name || 'Institution'}
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">Ownership Chain</h1>
        <p className="mt-2 text-gray-600">
          Corporate ownership structure from entity to ultimate parent
        </p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card p-4">
          <p className="text-sm text-gray-500">Total Depth</p>
          <p className="text-xl font-semibold text-gray-900">
            {ownership.total_depth} levels
          </p>
        </div>
        <div className="card p-4">
          <p className="text-sm text-gray-500">Ultimate Parent</p>
          <Link
            to={`/institution/${ownership.ultimate_parent_lei}`}
            className="text-primary-600 hover:text-primary-700 font-mono text-sm"
          >
            {ownership.ultimate_parent_lei}
          </Link>
        </div>
      </div>

      {/* Ownership chain visualization */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">
          Ownership Hierarchy
        </h2>

        {isOwnParent ? (
          <div className="text-center py-8 text-gray-500">
            <p>This entity is its own ultimate parent.</p>
            <p className="text-sm mt-2">No parent relationships found in the database.</p>
          </div>
        ) : (
          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 top-8 bottom-8 w-0.5 bg-gray-200" />

            {/* Chain nodes */}
            <div className="space-y-6">
              {ownership.chain.map((node, index) => {
                const isFirst = index === 0;
                const isLast = index === ownership.chain.length - 1;
                const isCurrent = node.lei === lei;

                return (
                  <div key={node.lei} className="relative flex items-start gap-4">
                    {/* Node indicator */}
                    <div
                      className={`relative z-10 w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold ${
                        isCurrent
                          ? 'bg-primary-600'
                          : isFirst
                          ? 'bg-gray-900'
                          : 'bg-gray-400'
                      }`}
                    >
                      {node.depth}
                    </div>

                    {/* Node content */}
                    <div className="flex-1 card p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Link
                            to={`/institution/${node.lei}`}
                            className="font-medium text-gray-900 hover:text-primary-600"
                          >
                            {node.legal_name}
                          </Link>
                          <p className="text-sm text-gray-500 font-mono mt-1">
                            {node.lei}
                          </p>
                        </div>
                        <div className="text-right">
                          {node.ownership_percentage && (
                            <span className="inline-flex items-center px-2 py-1 rounded bg-primary-50 text-primary-700 text-sm font-medium">
                              {node.ownership_percentage.toFixed(1)}%
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Relationship type */}
                      {!isFirst && (
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                          <ChevronRight className="h-3 w-3" />
                          {node.relationship_type.replace(/_/g, ' ')}
                        </div>
                      )}

                      {/* Labels */}
                      <div className="flex gap-2 mt-2">
                        {isFirst && (
                          <span className="badge bg-gray-100 text-gray-700">
                            Ultimate Parent
                          </span>
                        )}
                        {isCurrent && (
                          <span className="badge bg-primary-100 text-primary-700">
                            Current Entity
                          </span>
                        )}
                        {isLast && !isCurrent && (
                          <span className="badge bg-gray-100 text-gray-700">
                            Direct Child
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
